// Strategy: who we are, the path to break-even, and each team's strategy, written from Kayla's strategy docs (Nov 4).
// Every section is optional, so a strategy that's half written still reads well, and gaps say so plainly.
import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Circle, Compass, ExternalLink, Flag, HelpCircle, ListChecks, Printer, Sparkles, Target, X } from "lucide-react";
import { ago, api, Empty, Loading, Pill, setQuery, useLoad, useMe, useRoute } from "./lib";

const list = (v: unknown): any[] => (Array.isArray(v) ? v.filter((x) => x != null && x !== "") : []);
// "2026-11-05" reads as "Thu, Nov 5"; anything else ("Nov 16 to 19", "TBD") stays as written.
const when = (v: unknown) => {
  const m = typeof v === "string" && /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (!m) return String(v ?? "");
  return new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, 12)).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
};
const realOwner = (v: unknown) => (typeof v === "string" && v.trim() && !/^n\/?a$/i.test(v.trim()) ? v : null);
const safeHref = (h: unknown) => (typeof h === "string" && /^https:\/\//.test(h) ? h : null);
const STATUS: Record<string, [string, string]> = {
  done: ["good", "Done"], decided: ["good", "Decided"], planned: ["info", "Planned"], proposed: ["warn", "Proposed"], unclear: ["", "Unclear"],
};

function Section({ title, icon, children, tone }: { title: string; icon?: React.ReactNode; children: React.ReactNode; tone?: string }) {
  return (
    <section className={`card stack strat-sec ${tone ?? ""}`}>
      <div className="row" style={{ gap: 8 }}>{icon}<h2>{title}</h2></div>
      {children}
    </section>
  );
}

function Bullets({ items }: { items: any[] }) {
  return <ul className="strat-list">{items.map((x, i) => <li key={i}>{typeof x === "string" ? x : x.text ?? x.rule ?? JSON.stringify(x)}</li>)}</ul>;
}

// What Kayla decided in Today, newest first.
function Decided({ items }: { items: any[] }) {
  if (!items.length) return null;
  return (
    <Section title="Decided" icon={<CheckCircle2 size={18} className="muted" />}>
      <div className="strat-time">
        {items.map((x, i) => (
          <div key={i} className="strat-step">
            <time>{when(x.on)}</time>
            <div className="grow">
              <div>{x.question}</div>
              <div className="small"><b>{x.choice}</b>{x.note ? <span className="muted"> · {x.note}</span> : null}</div>
            </div>
            <Pill tone="good">Decided</Pill>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Sources({ items, updated }: { items: any[]; updated?: string | null }) {
  if (!items.length && !updated) return null;
  return (
    <footer className="strat-sources">
      <span className="tiny muted">Written from</span>
      {items.map((s, i) => {
        const href = safeHref(s.href);
        const label = typeof s === "string" ? s : `${s.label}${s.date ? ` (${String(s.date).slice(0, 10)})` : ""}`;
        return href ? <a key={i} className="pill" href={href} target="_blank" rel="noreferrer">{label} <ExternalLink size={11} /></a> : <span key={i} className="pill">{label}</span>;
      })}
      {updated && <span className="tiny muted">· Updated in the portal {ago(updated)}</span>}
    </footer>
  );
}

// The shape every team strategy shares: one line, goal, where we are, big moves, timeline, numbers, open calls.
function TeamStrategy({ d }: { d: any }) {
  const goal = d.goal ?? {};
  const pillars = list(d.pillars);
  const timeline = list(d.timeline);
  const numbers = list(d.numbers);
  const missing = [...list(d.gaps), ...list(d.conflicts)];
  return (
    <>
      <section className="card strat-hero">
        {d.owner && <div className="tiny muted">Owner: {d.owner}</div>}
        <p className="strat-one">{d.one_line || "Not written yet."}</p>
        {(goal.text || goal.target) && (
          <div className="strat-goal">
            <Target size={18} />
            <div className="grow">
              <div style={{ fontWeight: 600 }}>{goal.text}</div>
              <div className="small muted">{[goal.metric, goal.target && `Target: ${goal.target}`, goal.by && `By ${when(goal.by)}`].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
        )}
      </section>

      <div className="dept-grid">
        {list(d.where_we_are).length > 0 && <Section title="Where we are now" icon={<Compass size={18} className="muted" />}><Bullets items={list(d.where_we_are)} /></Section>}
        {list(d.audience).length > 0 && <Section title="Who it's for" icon={<Sparkles size={18} className="muted" />}><Bullets items={list(d.audience)} /></Section>}
      </div>

      {pillars.length > 0 && (
        <section className="stack">
          <h2 className="dept-h">The big moves</h2>
          <div className="grid">
            {pillars.map((p, i) => (
              <div key={i} className="card stack strat-pillar">
                <div className="strat-num">{i + 1}</div>
                <h3>{p.name}</h3>
                {p.what && <p className="small" style={{ margin: 0 }}>{p.what}</p>}
                {p.why && <p className="small muted" style={{ margin: 0 }}><b>Why:</b> {p.why}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {numbers.length > 0 && (
        <section className="stack">
          <h2 className="dept-h">The numbers</h2>
          <div className="grid-tight">
            {numbers.map((n, i) => (
              <div key={i} className="stat">
                <span className="label">{n.label}</span>
                <span className="value strat-val">{n.value || "Unknown"}</span>
                {n.source && <span className="src">{n.source}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {timeline.length > 0 && (
        <Section title="Timeline" icon={<Flag size={18} className="muted" />}>
          <div className="strat-time">
            {timeline.map((t, i) => {
              const [tone, label] = STATUS[t.status] ?? ["", t.status || ""];
              return (
                <div key={i} className="strat-step">
                  <time>{when(t.when)}</time>
                  <div className="grow">
                    <div>{t.what}</div>
                    {realOwner(t.owner) && <div className="tiny muted">{t.owner}</div>}
                  </div>
                  {label && <Pill tone={tone}>{label}</Pill>}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <div className="dept-grid">
        {list(d.decisions_open).length > 0 && (
          <Section title="Waiting on your decision" icon={<HelpCircle size={18} className="muted" />} tone="accent">
            <Bullets items={list(d.decisions_open)} />
            <a className="small" href="#/">Decide these in Today</a>
          </Section>
        )}
        {list(d.measures).length > 0 && <Section title="How we'll know it's working" icon={<CheckCircle2 size={18} className="muted" />}><Bullets items={list(d.measures)} /></Section>}
        {list(d.risks).length > 0 && <Section title="Risks to watch" icon={<AlertTriangle size={18} className="muted" />}><Bullets items={list(d.risks)} /></Section>}
      </div>

      <Decided items={list(d.decided)} />

      {missing.length > 0 && (
        <Section title="Not settled yet" icon={<HelpCircle size={18} className="muted" />} tone="quiet">
          <p className="small muted" style={{ margin: 0 }}>Things no doc covers yet, or where two docs disagree.</p>
          <Bullets items={missing} />
        </Section>
      )}
    </>
  );
}

function Foundation({ d }: { d: any }) {
  const quote = (label: string, x: any) =>
    x?.text ? (
      <div className="strat-quote">
        <div className="dept-h">{label}</div>
        <p>{x.text}</p>
      </div>
    ) : null;
  return (
    <>
      <section className="card strat-hero stack">
        {d.tagline?.text && <div className="strat-tag">{d.tagline.text}</div>}
        {quote("Mission", d.mission)}
        {quote("Vision", d.vision)}
      </section>
      {(d.north_star?.text || d.north_star?.metric) && (
        <section className="card strat-north">
          <Compass size={22} />
          <div className="grow">
            <div className="dept-h">North star</div>
            <p style={{ margin: "4px 0 0", fontSize: 17, fontWeight: 600 }}>{d.north_star.text}</p>
            {d.north_star.metric && <div className="small muted">Measured by: {d.north_star.metric}</div>}
          </div>
        </section>
      )}
      {list(d.values).length > 0 && (
        <section className="stack">
          <h2 className="dept-h">Our values</h2>
          <div className="grid">
            {list(d.values).map((v, i) => (
              <div key={i} className="card stack strat-pillar">
                <h3>{v.name}</h3>
                {v.meaning && <p className="small" style={{ margin: 0 }}>{v.meaning}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
      <div className="dept-grid">
        {list(d.how_we_decide).length > 0 && (
          <Section title="How we decide" icon={<CheckCircle2 size={18} className="muted" />} tone="accent">
            <ul className="strat-list">{list(d.how_we_decide).map((r, i) => <li key={i}><b>{r.rule}</b>{r.why ? <span className="muted"> {r.why}</span> : null}</li>)}</ul>
          </Section>
        )}
        {list(d.culture).length > 0 && <Section title="Our culture" icon={<Sparkles size={18} className="muted" />}><Bullets items={list(d.culture)} /></Section>}
        {list(d.voice).length > 0 && <Section title="How we sound" icon={<Sparkles size={18} className="muted" />}><Bullets items={list(d.voice)} /></Section>}
      </div>
      {(d.promise?.text || d.audience?.text) && (
        <div className="dept-grid">
          {d.promise?.text && <Section title="Our promise to moms"><p style={{ margin: 0 }}>{d.promise.text}</p></Section>}
          {d.audience?.text && <Section title="Who we serve"><p style={{ margin: 0 }}>{d.audience.text}</p></Section>}
        </div>
      )}
      <Decided items={list(d.decided)} />
      {[...list(d.gaps), ...list(d.conflicts), ...list(d.voice_flags)].length > 0 && (
        <Section title="Not settled yet" icon={<HelpCircle size={18} className="muted" />} tone="quiet">
          <Bullets items={[...list(d.gaps), ...list(d.conflicts), ...list(d.voice_flags)]} />
        </Section>
      )}
    </>
  );
}

function BreakEven({ d }: { d: any }) {
  const stats: [string, any][] = [["Paying users today", d.now], ["Paying users to break even", d.paying_users_needed], ["When", d.by_when]];
  const funnel = list(d.funnel);
  return (
    <>
      <div className="grid-tight">
        {stats.map(([label, v]) => (
          <div key={label} className="stat">
            <span className="label">{label}</span>
            <span className={`value ${v ? "" : "unknown"} ${String(v ?? "").length > 24 ? "strat-val" : ""}`}>{v ?? "Unknown"}</span>
          </div>
        ))}
      </div>
      {funnel.length > 0 && (
        <Section title="How a mom becomes a paying user" icon={<Flag size={18} className="muted" />}>
          <div className="strat-funnel">
            {funnel.map((f, i) => (
              <div key={i} className="strat-fstep">
                <div className="small muted">{f.step}</div>
                <div className="strat-val">{f.value || "Unknown"}</div>
                {f.source && <div className="tiny muted">{f.source}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}
      {list(d.notes).length > 0 && <Section title="What this means"><Bullets items={list(d.notes)} /></Section>}
      <Decided items={list(d.decided)} />
    </>
  );
}

export function Strategy() {
  const route = useRoute();
  const { data: index, error, reload: reloadIndex } = useLoad<any>("/api/strategy");
  const items: any[] = index?.items ?? [];
  const reviewing = route.q.get("s") === "tasks" && !!index?.can_edit;
  const key = reviewing ? null : items.find((s) => s.key === route.q.get("s"))?.key ?? items[0]?.key ?? null;
  const { data: one, error: oneError } = useLoad<any>(key ? `/api/strategy/${key}` : null, [key]);
  if (!index) return <div className="page"><Loading error={error} /></div>;
  const d = one?.key === key ? one?.data : undefined;
  return (
    <div className="page strat">
      <div className="row between wrap strat-bar">
        <div className="tabs scroll-x" role="tablist" aria-label="Strategies">
          {items.map((s) => (
            <button key={s.key} role="tab" aria-selected={s.key === key} className={[s.key === key ? "on" : "", s.written ? "" : "unwritten"].join(" ")} title={s.written ? undefined : "Not written yet"} onClick={() => setQuery({ s: s.key })}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="row" style={{ gap: 8, flex: "0 0 auto" }}>
          {index.can_edit && index.proposed > 0 && (
            <button className={`btn sm ${reviewing ? "" : "primary"}`} onClick={() => setQuery({ s: "tasks" })}><ListChecks size={14} /> Proposed tasks · {index.proposed}</button>
          )}
          <button className="btn sm hide-sm" onClick={() => window.print()}><Printer size={14} /> Print</button>
        </div>
      </div>
      {reviewing ? (
        <ProposedTasks onChanged={reloadIndex} />
      ) : (
        <>
          {key !== "foundation" && items.some((s) => s.key === "foundation" && s.written) && (
            <div className="banner info small"><Compass size={16} /> Check every move here against <a href="#/strategy?s=foundation">who we are</a>: our mission, values and north star.</div>
          )}
          {one?.key !== key ? <Loading error={oneError} /> : !d ? (
            <Empty>This strategy isn't written down yet.</Empty>
          ) : (
            <>
              {key === "foundation" ? <Foundation d={d} /> : key === "break_even" ? <BreakEven d={d} /> : <TeamStrategy d={d} />}
              <StrategyTasks tasks={list(one.tasks)} />
              <Sources items={list(d.sources)} updated={one.updated_at} />
            </>
          )}
        </>
      )}
    </div>
  );
}

// The approved tasks a strategy became, so the page shows whether it's moving.
function StrategyTasks({ tasks }: { tasks: any[] }) {
  if (!tasks.length) return null;
  const done = tasks.filter((t) => t.status === "done").length;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Section title={`Doing it · ${done} of ${tasks.length} done`} icon={<ListChecks size={18} className="muted" />}>
      <div className="progress" style={{ height: 6 }}><div style={{ width: `${Math.round((done / tasks.length) * 100)}%` }} /></div>
      <div className="strat-time">
        {tasks.map((t) => {
          const late = t.status !== "done" && t.due_on && t.due_on < today;
          return (
            <a key={t.id} className="strat-step" href={`#/tasks?scope=everyone&task=${t.id}`} style={{ color: "inherit", textDecoration: "none" }}>
              <time>{t.due_on ? when(t.due_on) : "No date"}</time>
              <div className="grow"><div>{t.title}</div><div className="tiny muted">{t.owner}</div></div>
              <Pill tone={t.status === "done" ? "good" : late ? "bad" : ""}>{t.status === "done" ? "Done" : late ? "Overdue" : t.status === "in_progress" ? "In progress" : t.status === "blocked" ? "Blocked" : "To do"}</Pill>
            </a>
          );
        })}
      </div>
    </Section>
  );
}

// Kayla's review: every proposed task grouped by who would get it. She can change the person, date or wording,
// untick what she doesn't want yet, or skip one. Only what she approves reaches anyone's list.
function ProposedTasks({ onChanged }: { onChanged: () => void }) {
  const { toast } = useMe();
  const { data, error, reload } = useLoad<any>("/api/strategy/proposals");
  const [edits, setEdits] = useState<Record<string, { on: boolean; owner_id: string; due_on: string; title: string }>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!data) return;
    setEdits((prev) => Object.fromEntries(data.items.map((p: any) => [p.id, prev[p.id] ?? { on: true, owner_id: p.owner_id, due_on: p.due_on ?? "", title: p.title }])));
  }, [data]);
  if (!data) return <Loading error={error} />;
  if (!data.items.length) return <Empty>No proposed tasks waiting. Everything has been approved or skipped.</Empty>;
  const people: any[] = data.people;
  const nameOf = (id: string) => people.find((u) => u.id === id)?.name ?? "Someone";
  const rows = data.items.filter((p: any) => edits[p.id]);
  const groups = [...new Set(rows.map((p: any) => edits[p.id]!.owner_id))] as string[];
  const chosen = rows.filter((p: any) => edits[p.id]!.on);
  const set = (id: string, patch: Partial<{ on: boolean; owner_id: string; due_on: string; title: string }>) => setEdits((e) => ({ ...e, [id]: { ...e[id]!, ...patch } }));
  const send = async (payload: any, msg: string) => {
    setBusy(true);
    try {
      const r = await api("/api/strategy/proposals/decide", { method: "POST", body: payload });
      toast(msg.replace("{n}", String(r.approved ?? r.skipped)));
      reload();
      onChanged();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <section className="card stack">
        <div className="row" style={{ gap: 8 }}><ListChecks size={18} className="muted" /><h2>Proposed tasks from the strategies</h2></div>
        <p className="small muted" style={{ margin: 0 }}>
          Grouped by who would get each one. Change the person, date or wording, or untick anything you don't want yet. Nothing goes on anyone's list until you approve it, and each person gets a notification when you do.
        </p>
        <div className="row wrap" style={{ gap: 8 }}>
          <button className="btn primary" disabled={busy || !chosen.length} onClick={() => send({ approve: chosen.map((p: any) => ({ id: p.id, ...edits[p.id] })) }, "Approved {n} tasks. They're on each person's list now.")}>
            Approve {chosen.length} task{chosen.length === 1 ? "" : "s"}
          </button>
          <span className="small muted">Unticked ones stay here for later.</span>
        </div>
      </section>
      {groups.map((owner) => (
        <section key={owner} className="card stack">
          <h2>{nameOf(owner)} · {rows.filter((p: any) => edits[p.id]!.owner_id === owner).length}</h2>
          <div className="strat-time">
            {rows.filter((p: any) => edits[p.id]!.owner_id === owner).map((p: any) => {
              const e = edits[p.id]!;
              return (
                <div key={p.id} className={`strat-propose ${e.on ? "" : "off"}`}>
                  <button className="icon-btn" aria-label={e.on ? "Leave this one for later" : "Include this one"} onClick={() => set(p.id, { on: !e.on })}>
                    {e.on ? <CheckCircle2 size={20} className="strat-on" /> : <Circle size={20} className="muted" />}
                  </button>
                  <div className="grow stack" style={{ gap: 6, minWidth: 0 }}>
                    <input className="input strat-title" value={e.title} onChange={(ev) => set(p.id, { title: ev.target.value })} aria-label="Task" />
                    {p.why && <div className="small muted">{p.why}</div>}
                    <div className="row wrap" style={{ gap: 8 }}>
                      <a className="pill accent" href={`#/strategy?s=${p.strategy}`}>{p.strategy_label}</a>
                      <select className="input" style={{ width: "auto" }} value={e.owner_id} onChange={(ev) => set(p.id, { owner_id: ev.target.value })} aria-label="Who">
                        {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                      <input className="input" type="date" style={{ width: "auto" }} value={e.due_on} onChange={(ev) => set(p.id, { due_on: ev.target.value })} aria-label="Due" />
                    </div>
                  </div>
                  <button className="icon-btn" aria-label="Skip this task" title="Skip: don't make this task" disabled={busy} onClick={() => send({ skip: [p.id] }, "Skipped.")}><X size={16} /></button>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}

// The weekly scorecard at the top of the Weekly review: one row per strategy, worked out live.
const SCORE: Record<string, [string, string]> = { moving: ["good", "Moving"], slipping: ["bad", "Slipping"], quiet: ["warn", "Quiet"], not_started: ["", "No tasks yet"] };
export function StrategyScorecard() {
  const { data } = useLoad<any>("/api/strategy/scorecard");
  if (!data || !data.strategies.length) return null;
  return (
    <section className="card stack">
      <div className="row between wrap" style={{ gap: 8 }}>
        <div className="row" style={{ gap: 8 }}><ListChecks size={18} className="muted" /><h2>Strategy scorecard</h2></div>
        <span className="tiny muted">Live from the portal · last 7 days and the next 7</span>
      </div>
      <div className="strat-time">
        {data.strategies.map((s: any) => {
          const [tone, label] = SCORE[s.status] ?? ["", s.status];
          const line = [
            s.total ? `${s.done.length} done this week, ${s.open} open` : null,
            s.overdue.length ? `${s.overdue.length} overdue` : null,
            s.due_soon.length ? `${s.due_soon.length} due this week` : null,
            s.decided_this_week ? `${s.decided_this_week} decided this week` : null,
            s.waiting_on_kayla ? `${s.waiting_on_kayla} waiting on Kayla` : null,
          ].filter(Boolean).join(" · ");
          const items = [...s.overdue.map((t: any) => ({ ...t, late: true })), ...s.due_soon];
          return (
            <div key={s.key} className="strat-step" style={{ display: "block" }}>
              <div className="row between wrap" style={{ gap: 8 }}>
                <a href={`#/strategy?s=${s.key}`} style={{ fontWeight: 600 }}>{s.label}</a>
                <Pill tone={tone}>{label}</Pill>
              </div>
              <div className="small muted" style={{ marginTop: 2 }}>{line || "Nothing tracked yet."}</div>
              {items.length > 0 && (
                <ul className="strat-list" style={{ marginTop: 8 }}>
                  {items.slice(0, 6).map((t: any) => (
                    <li key={t.id}>
                      <a href={`#/tasks?scope=everyone&task=${t.id}`} style={{ color: "inherit" }}>{t.title}</a>
                      <span className={`tiny ${t.late ? "" : "muted"}`} style={t.late ? { color: "var(--bad)" } : undefined}> · {t.owner} · {t.late ? "overdue since " : "due "}{when(t.due_on)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.coming_up.length > 0 && (
                <div className="tiny muted" style={{ marginTop: 6 }}>On the plan this week: {s.coming_up.map((c: any) => `${when(c.when)}: ${c.what}`).join(" · ")}</div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
